
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CookieConsent } from "@/lib/i18n";
import { SupportedLanguage, LanguageSelector } from "@/components/multilingual/LanguageSelector";
import { translations } from "@/lib/i18n";
import CookieSettingsTab from "./CookieSettingsTab";

interface CookieSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  language: SupportedLanguage;
  consent: CookieConsent;
  onSwitchChange: (category: keyof CookieConsent) => void;
  onSave: () => void;
  onClose: () => void;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

export function CookieSettingsDialog({
  open,
  onOpenChange,
  language,
  consent,
  onSwitchChange,
  onSave,
  onClose,
  onLanguageChange
}: CookieSettingsDialogProps) {
  const t = translations.cookieBanner[language];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.cookieSettings}</DialogTitle>
          <DialogDescription>
            {t.description}
          </DialogDescription>
          {onLanguageChange && (
            <div className="pt-2">
              <LanguageSelector 
                language={language} 
                onChange={onLanguageChange}
                className="w-full"
              />
            </div>
          )}
        </DialogHeader>
        
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="all" className="flex-1">{t.all}</TabsTrigger>
            <TabsTrigger value="essential" className="flex-1">{t.essential}</TabsTrigger>
            <TabsTrigger value="optional" className="flex-1">{t.optional}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="space-y-4 mt-4">
            {/* All cookie options */}
            <CookieSettingsTab 
              id="necessary"
              title={t.necessary}
              description={t.necessaryDescription}
              checked={consent.necessary}
              onChange={() => onSwitchChange('necessary')}
              disabled={true}
            />
            <CookieSettingsTab 
              id="analytics"
              title={t.analytics}
              description={t.analyticsDescription}
              checked={consent.analytics}
              onChange={() => onSwitchChange('analytics')}
            />
            <CookieSettingsTab 
              id="marketing"
              title={t.marketing}
              description={t.marketingDescription}
              checked={consent.marketing}
              onChange={() => onSwitchChange('marketing')}
            />
            <CookieSettingsTab 
              id="preferences"
              title={t.preferences}
              description={t.preferencesDescription}
              checked={consent.preferences}
              onChange={() => onSwitchChange('preferences')}
            />
          </TabsContent>
          
          <TabsContent value="essential" className="space-y-4 mt-4">
            {/* Just Necessary Cookies */}
            <CookieSettingsTab 
              id="necessary-tab"
              title={t.necessary}
              description={t.necessaryDescription}
              checked={consent.necessary}
              onChange={() => {}}
              disabled={true}
            />
          </TabsContent>
          
          <TabsContent value="optional" className="space-y-4 mt-4">
            {/* Optional cookies */}
            <CookieSettingsTab 
              id="analytics-tab"
              title={t.analytics}
              description={t.analyticsDescription}
              checked={consent.analytics}
              onChange={() => onSwitchChange('analytics')}
            />
            <CookieSettingsTab 
              id="marketing-tab"
              title={t.marketing}
              description={t.marketingDescription}
              checked={consent.marketing}
              onChange={() => onSwitchChange('marketing')}
            />
            <CookieSettingsTab 
              id="preferences-tab"
              title={t.preferences}
              description={t.preferencesDescription}
              checked={consent.preferences}
              onChange={() => onSwitchChange('preferences')}
            />
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t.close}
          </Button>
          <Button onClick={onSave}>
            {t.save}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CookieSettingsDialog;
